import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { ConfirmProvider } from "@/lib/ConfirmProvider";

import { AuthProvider } from "@/context/AuthContext";
import { UxProvider } from "@/lib/UxProvider";
import ProtectedRoute from "@/components/protected/ProtectedRoute";
import RouteErrorBoundary from "@/components/common/RouteErrorBoundary";
import { ColorBlindFilters } from "@/components/a11y/ColorBlindFilters";
// P166 — deferred from the eager main bundle (not needed for first paint on Login/Landing); loaded
// via Suspense like the page routes. Delivery-mechanics only — no behavior/UI change.
const WorkspaceShell = lazy(() => import("@/components/layout/WorkspaceShell"));
const CommandPalette = lazy(() => import("@/components/navigation/CommandPalette").then((m) => ({ default: m.CommandPalette })));
const HelpAssistant = lazy(() => import("@/components/support/HelpAssistant"));
const HelpCenter = lazy(() => import("@/pages/help/HelpCenter"));

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import DataProtectionPolicyPage from "@/pages/legal/DataProtectionPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";

const ExecutiveCommandCenter = lazy(() => import("@/pages/ecc/ExecutiveCommandCenter"));
const AppHomeIndex = lazy(() => import("@/pages/AppHomeIndex"));
const PosTerminal = lazy(() => import("@/pages/pos/PosTerminal"));
const StoreOpsToday = lazy(() => import("@/pages/store/StoreOpsToday"));
const SalespersonPerformance = lazy(() => import("@/pages/store/SalespersonPerformance"));
const Inventory = lazy(() => import("@/pages/inventory/Inventory"));
const Orders = lazy(() => import("@/pages/orders/Orders"));
// P95 — DecisionOS page no longer routed (see redirect note near DecisionHome import).

const AIProviderConfig = lazy(() => import("@/pages/admin/AIProviderConfig"));
const Tenants = lazy(() => import("@/pages/admin/Tenants"));
const Users = lazy(() => import("@/pages/admin/Users"));
const AccountDeletionRequests = lazy(() => import("@/pages/admin/AccountDeletionRequests"));
const LegalVersions = lazy(() => import("@/pages/admin/LegalVersions"));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const BackgroundJobs = lazy(() => import("@/pages/admin/BackgroundJobs"));
const AffinityPerf = lazy(() => import("@/pages/admin/AffinityPerf"));
const DemoData = lazy(() => import("@/pages/admin/DemoData"));

// Phase 0 — Foundation
const FoundationOverview = lazy(() => import("@/pages/foundation/Overview"));
const NotificationsInbox = lazy(() => import("@/pages/foundation/NotificationsInbox"));
import {
  Organizations, Companies, Branches, Stores, Currencies, ExchangeRates, Languages,
  Timezones, Countries, SubscriptionPlans, FeatureFlags, Configurations,
  NumberSeries, NotificationTemplates, Documents, AIModels, Prompts, ApiRegistry,
} from "@/pages/foundation/pages";

// Phase 0A — UX Foundation
const DesignSystem = lazy(() => import("@/pages/design-system/DesignSystem"));
const Appearance = lazy(() => import("@/pages/settings/Appearance"));
const BrandingSettings = lazy(() => import("@/pages/settings/BrandingSettings"));
const DeviceSettings = lazy(() => import("@/pages/settings/DeviceSettings"));
const CommunicationsSettings = lazy(() => import("@/pages/settings/CommunicationsSettings"));
const PaymentGatewaySettings = lazy(() => import("@/pages/settings/PaymentGatewaySettings"));
const PosSetupWizard = lazy(() => import("@/pages/pos/PosSetupWizard"));
const CustomerDisplay = lazy(() => import("@/pages/pos/CustomerDisplay"));
const PosLockScreen = lazy(() => import("@/pages/pos/PosLockScreen"));
const PosFastLogin = lazy(() => import("@/pages/pos/PosFastLogin"));
const PosShell = lazy(() => import("@/pages/pos/PosShell"));
const PosOpsHome = lazy(() => import("@/pages/pos-ops/PosOpsHome"));
const PosAdminSettings = lazy(() => import("@/pages/pos-admin/PosAdminSettings"));
const StoreOnboardingWizard = lazy(() => import("@/pages/foundation/StoreOnboardingWizard"));
import { BrandingProvider } from "@/context/BrandingContext";
import AutoTranslate from "@/lib/AutoTranslate";

// Phase 0B — Reporting Framework
const ReportCatalog = lazy(() => import("@/pages/reports/ReportCatalog"));
const ReportVisibilityMatrix = lazy(() => import("@/pages/reports/ReportVisibilityMatrix"));
const ModuleReports = lazy(() => import("@/pages/reports/ModuleReports"));
const ReportDesigner = lazy(() => import("@/pages/reports/ReportDesigner"));
const ReportViewer = lazy(() => import("@/pages/reports/ReportViewer"));
const AIReportBuilder = lazy(() => import("@/pages/reports/AIReportBuilder"));
const ReportSchedules = lazy(() => import("@/pages/reports/SchedulesAndAnalytics").then((m) => ({ default: m.Schedules })));
const ReportAnalytics = lazy(() => import("@/pages/reports/SchedulesAndAnalytics").then((m) => ({ default: m.ReportAnalytics })));

// Phase 0C — Workflow / BPM
const WorkflowCatalog = lazy(() => import("@/pages/workflows/Workflows").then((m) => ({ default: m.WorkflowCatalog })));
const WorkflowInbox = lazy(() => import("@/pages/workflows/Workflows").then((m) => ({ default: m.WorkflowInbox })));
const WorkflowInstance = lazy(() => import("@/pages/workflows/Workflows").then((m) => ({ default: m.WorkflowInstance })));
const WorkflowAnalytics = lazy(() => import("@/pages/workflows/Workflows").then((m) => ({ default: m.WorkflowAnalytics })));
const RulesLibrary = lazy(() => import("@/pages/workflows/Workflows").then((m) => ({ default: m.RulesLibrary })));

// Phase 1 — Security & Identity
const SecurityCenter = lazy(() => import("@/pages/security/SecurityCenter"));
const RouteAuthz = lazy(() => import("@/pages/admin/RouteAuthz"));
const BackupDR = lazy(() => import("@/pages/backup/BackupDR"));
const MySecurity = lazy(() => import("@/pages/security/MySecurity"));

// Phase 2 — Product Foundation
const ProductFoundation = lazy(() => import("@/pages/products/ProductFoundation"));
const ProductDetail = lazy(() => import("@/pages/products/ProductDetail"));

// Phase 2A — PIM Studio
const PimStudio = lazy(() => import("@/pages/pim/PimStudio"));
const Channels = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.Channels })));
const Marketplaces = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.Marketplaces })));
const AttributeSets = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.AttributeSets })));
const ClassificationSystems = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.ClassificationSystems })));
const Catalogs = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.Catalogs })));
const ValidationRules = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.ValidationRules })));
const ContentTemplates = lazy(() => import("@/pages/pim/managers").then((m) => ({ default: m.ContentTemplates })));
const TaxonomyManager = lazy(() => import("@/pages/pim/TaxonomyManager"));
const PublicationCenter = lazy(() => import("@/pages/pim/PublicationCenter"));
const ApprovalCenter = lazy(() => import("@/pages/pim/ApprovalCenter"));
const QualityCenter = lazy(() => import("@/pages/pim/QualityCenter"));

// Phase 2B — DAM Studio
const DamStudio = lazy(() => import("@/pages/dam/DamStudio"));
const AssetRepository = lazy(() => import("@/pages/dam/AssetRepository"));
const AssetDetail = lazy(() => import("@/pages/dam/AssetDetail"));
const CollectionManager = lazy(() => import("@/pages/dam/CollectionManager"));
const DamApprovalCenter = lazy(() => import("@/pages/dam/DamApprovalCenter"));
const StorageManager = lazy(() => import("@/pages/dam/StorageManager"));

// Phase 2C — MDM Studio
const MdmStudio = lazy(() => import("@/pages/mdm/MdmStudio"));
const DomainManager = lazy(() => import("@/pages/mdm/DomainManager"));
const GoldenRecordManager = lazy(() => import("@/pages/mdm/GoldenRecordManager"));
const ReferenceDataManager = lazy(() => import("@/pages/mdm/ReferenceDataManager"));
const HierarchyManager = lazy(() => import("@/pages/mdm/HierarchyManager"));
const DuplicateCenter = lazy(() => import("@/pages/mdm/DuplicateCenter"));
const MergeWorkbench = lazy(() => import("@/pages/mdm/MergeWorkbench"));
const MdmQualityCenter = lazy(() => import("@/pages/mdm/QualityCenter"));
const GovernanceCenter = lazy(() => import("@/pages/mdm/GovernanceCenter"));
const StewardWorkbench = lazy(() => import("@/pages/mdm/StewardWorkbench"));
const LineageExplorer = lazy(() => import("@/pages/mdm/LineageExplorer"));
const MdmAiStudio = lazy(() => import("@/pages/mdm/MdmAiStudio"));

// Phase 3 — Customer Foundation
const CustomerMaster = lazy(() => import("@/pages/customers/CustomerMaster"));
const CustomerProfile = lazy(() => import("@/pages/customers/CustomerProfile"));
const CustomerStructure = lazy(() => import("@/pages/customers/CustomerStructure"));
const CustomerDashboard = lazy(() => import("@/pages/customers/CustomerDashboard"));

// Phase 4 — Supplier & Vendor Foundation
const VendorMaster = lazy(() => import("@/pages/vendors/VendorMaster"));
const VendorProfile = lazy(() => import("@/pages/vendors/VendorProfile"));
const VendorStructure = lazy(() => import("@/pages/vendors/VendorStructure"));
const VendorGovernance = lazy(() => import("@/pages/vendors/VendorGovernance"));
const VendorPerformance = lazy(() => import("@/pages/vendors/VendorPerformance"));
const VendorDashboard = lazy(() => import("@/pages/vendors/VendorDashboard"));

// Phase 4F — External Vendor Portal (custom JWT, separate auth)
import { VendorPortalProvider } from "@/context/VendorPortalContext";
const VendorPortalLogin = lazy(() => import("@/pages/portal/VendorPortalLogin"));
const VendorPortal = lazy(() => import("@/pages/portal/VendorPortal"));

// Phase 24 — Enterprise Integration Fabric
const IntegrationFabric = lazy(() => import("@/pages/integration/IntegrationFabric"));

// Phase 25 — Enterprise Analytics Platform
const AnalyticsHome = lazy(() => import("@/pages/analytics/AnalyticsHome"));
const TargetManagement = lazy(() => import("@/pages/target-management/TargetManagement"));
const IncentiveSchemes = lazy(() => import("@/pages/incentive-schemes/IncentiveSchemes"));
const MyPerformance = lazy(() => import("@/pages/salesperson/MyPerformance"));

// Phase 31 — Enterprise Control Towers Platform
const ControlTowerHome = lazy(() => import("@/pages/control-tower/ControlTowerHome"));

// Phase 32 — Enterprise Executive Cockpits Platform
const ExecutiveHome = lazy(() => import("@/pages/executive/ExecutiveHome"));

// Phase 33 — Enterprise Digital Twin Platform
const DigitalTwinHome = lazy(() => import("@/pages/digital-twin/DigitalTwinHome"));
const CommandCenterHome = lazy(() => import("@/pages/command-center/CommandCenterHome"));
const WorkHub = lazy(() => import("@/pages/workbench/WorkHub"));
const CompanyLink = lazy(() => import("@/pages/finance/CompanyLink"));
const ProductRegistryHome = lazy(() => import("@/pages/registry/ProductRegistryHome"));
const CommercialGovernanceHome = lazy(() => import("@/pages/commercial/CommercialHome"));
const ExperienceHome = lazy(() => import("@/pages/experience/ExperienceHome"));
const MyWorkspace = lazy(() => import("@/pages/experience/MyWorkspace"));
const SecurityHome = lazy(() => import("@/pages/security/SecurityHome"));
const AiConsumptionHome = lazy(() => import("@/pages/aigov/AiConsumptionHome"));
const DSARConsole = lazy(() => import("@/pages/dsar/DSARConsole"));
const TenantConsumptionPortal = lazy(() => import("@/pages/aigov/TenantConsumptionPortal"));

// PlatformCore Governance PG-06 — Enterprise Marketplace & App Exchange
const MarketEcosystemHome = lazy(() => import("@/pages/market/MarketEcosystemHome"));

// Phase 26 — Enterprise Data Platform & Lakehouse
const DataPlatformHome = lazy(() => import("@/pages/data/DataPlatformHome"));

// Phase 5 — Enterprise Inventory Foundation
const InventoryFoundation = lazy(() => import("@/pages/inventory-platform/InventoryFoundation"));

// Phase 6 — Enterprise Warehouse Management System
const WmsHome = lazy(() => import("@/pages/wms/WmsHome"));

// Phase 7 — Enterprise Procurement Management
const ProcurementHome = lazy(() => import("@/pages/procurement/ProcurementHome"));

// Phase 8 — Enterprise Merchandising Foundation
const MerchandisingHome = lazy(() => import("@/pages/merchandising/MerchandisingHome"));

// Phase 8A — Merchandise Financial Planning & Open-To-Buy
const MfpHome = lazy(() => import("@/pages/mfp/MfpHome"));

// Phase 8B — Enterprise Allocation Optimization
const AllocationHome = lazy(() => import("@/pages/allocation/AllocationHome"));

// Phase 8C — Enterprise Assortment Planning
const AssortmentHome = lazy(() => import("@/pages/assortment/AssortmentHome"));

// Phase 8D — Enterprise Replenishment Intelligence
const ReplenishmentHome = lazy(() => import("@/pages/replenishment/ReplenishmentHome"));

// Phase 8E — Enterprise Pricing Management
const PricingHome = lazy(() => import("@/pages/pricing/PricingHome"));

// Phase 8F — Enterprise Promotion & Campaign Management
const PromotionHome = lazy(() => import("@/pages/promotions/PromotionHome"));

// Phase 9 — Enterprise POS Core Platform (Store Commerce)
const PosCoreHome = lazy(() => import("@/pages/pos-core/PosCoreHome"));

// Phase 9A — Enterprise Offline POS Platform
const OfflineHome = lazy(() => import("@/pages/offline-pos/OfflineHome"));

// Phase 9B — Enterprise Mobile POS (mPOS) Platform
const MposHome = lazy(() => import("@/pages/mpos/MposHome"));

// Phase 9C — Enterprise Store Edge Computing Platform
const EdgeHome = lazy(() => import("@/pages/edge/EdgeHome"));

// Phase 9D — Enterprise Device Management Platform
const DmpHome = lazy(() => import("@/pages/dmp/DmpHome"));

// Phase 9E — Enterprise Hardware Integration Platform
const HardwareHome = lazy(() => import("@/pages/hardware/HardwareHome"));

// Phase 10 — Enterprise Self Checkout Platform
const ScoHome = lazy(() => import("@/pages/sco/ScoHome"));
const KioskApp = lazy(() => import("@/pages/sco/kiosk/KioskApp"));
const ScangoHome = lazy(() => import("@/pages/scango/ScangoHome"));
const ShopApp = lazy(() => import("@/pages/scango/shop/ShopApp"));
const SmartcoHome = lazy(() => import("@/pages/smartco/SmartcoHome"));
const SmartStoreApp = lazy(() => import("@/pages/smartco/store/SmartStoreApp"));
const AicoHome = lazy(() => import("@/pages/aico/AicoHome"));
const AiShopperApp = lazy(() => import("@/pages/aico/shopper/AiShopperApp"));
const LpHome = lazy(() => import("@/pages/lossprev/LpHome"));

// Phase 10E — Enterprise RFID Platform
const RfidHome = lazy(() => import("@/pages/rfid/RfidHome"));

// Phase 10F — Enterprise Computer Vision Platform
const VisionHome = lazy(() => import("@/pages/vision/VisionHome"));

// Phase 10G — Enterprise Retail Audit Platform
const AuditHome = lazy(() => import("@/pages/audit/AuditHome"));

// Phase 11 — Enterprise Omnichannel Commerce Platform
const OmniHome = lazy(() => import("@/pages/omni/OmniHome"));

// Phase 11A — Enterprise Marketplace Platform
const MarketplaceHome = lazy(() => import("@/pages/marketplace/MarketplaceHome"));

// Phase 11B — Enterprise Marketplace Seller Platform
const SellerPlatformHome = lazy(() => import("@/pages/seller/SellerPlatformHome"));

// Phase 12 — Enterprise Loyalty, Rewards & Customer Engagement Platform
const LoyaltyHome = lazy(() => import("@/pages/loyalty/LoyaltyHome"));

// Phase 13 — Enterprise CRM & Customer 360 Platform
const CrmHome = lazy(() => import("@/pages/crm/CrmHome"));

// Phase 14 — Enterprise Marketing Automation Platform
const MarketingHome = lazy(() => import("@/pages/marketing/MarketingHome"));

// Phase 14A — Enterprise Customer Data Platform (CDP)
const CdpHome = lazy(() => import("@/pages/cdp/CdpHome"));

// Phase 14B — Enterprise Customer Experience Management (CXM)
const CxmHome = lazy(() => import("@/pages/cxm/CxmHome"));

// Phase 15 — Enterprise Finance Core Platform
const FinanceHome = lazy(() => import("@/pages/finance/FinanceHome"));

// Phase 16 — Enterprise Advanced Finance Platform
const AdvFinHome = lazy(() => import("@/pages/advfin/AdvFinHome"));

// Phase 17 — Enterprise Workforce Management Platform
const WorkforceHome = lazy(() => import("@/pages/workforce/WorkforceHome"));

// Phase 18 — Enterprise Supply Chain Planning Platform
const SupplyChainHome = lazy(() => import("@/pages/scp/SupplyChainHome"));

// Phase 19 — Enterprise Transportation Management System
const TransportationHome = lazy(() => import("@/pages/transportation/TransportationHome"));

// Phase 20 — Enterprise Pricing, TPM & RGM Platform
const CommercialHome = lazy(() => import("@/pages/rgm/CommercialHome"));

// Phase 27 — Enterprise AI/ML Platform
const AiHome = lazy(() => import("@/pages/aiml/AiHome"));
const GenaiHome = lazy(() => import("@/pages/genai/GenaiHome"));
const AgenticHome = lazy(() => import("@/pages/agentic/AgenticHome"));
// P95 — DecisionOS.jsx is intentionally left orphaned (unrouted) for one release cycle in case the
// /app/decision-os → /app/decisionos redirect needs a rollback. Follow-up: delete the file later.
const DecisionHome = lazy(() => import("@/pages/decisionos/DecisionHome"));

// REMS — Return Evidence Management System (VMS for Return QC)
const RemsHome = lazy(() => import("@/pages/rems/RemsHome"));

// REASON TailorStudio™ — Bespoke / Made-to-Measure garment management
const TailorHome = lazy(() => import("@/pages/tailor/TailorHome"));
const TailorPortal = lazy(() => import("@/pages/tailor/TailorPortal"));

// P24 — intentional crash route used to verify RouteErrorBoundary catches render errors.
function CrashProbe() {
  throw new Error("CrashProbe: intentional render error for ErrorBoundary verification");
}

export default function App() {
  return (
    <UxProvider>
      <AuthProvider>
        <BrandingProvider>
        <BrowserRouter>
        <ConfirmProvider>
          <Toaster position="top-right" richColors closeButton />
          <ColorBlindFilters />
          <AutoTranslate />
          <Suspense fallback={null}>
            <CommandPalette />
            <HelpAssistant />
          </Suspense>
          <RouteErrorBoundary name="Application">
          <Suspense fallback={<div className="p-10 text-sm text-muted-foreground">Loading…</div>}>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/data-protection-policy" element={<DataProtectionPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />

          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <WorkspaceShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AppHomeIndex />} />
            <Route path="pos" element={<Navigate to="/pos-app" replace />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="orders" element={<Orders />} />
            <Route path="decision-os" element={<Navigate to="/app/decisionos?tab=recommendation" replace />} />
            <Route
              path="admin/ai-providers"
              element={
                <ProtectedRoute requireRole={["superadmin", "admin"]}>
                  <AIProviderConfig />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/tenants"
              element={
                <ProtectedRoute requireRole={["superadmin"]}>
                  <Tenants />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute requireRole={["superadmin", "admin"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/audit"
              element={
                <ProtectedRoute requireRole={["superadmin", "admin"]}>
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/background-jobs"
              element={
                <ProtectedRoute requireRole={["superadmin", "admin"]}>
                  <BackgroundJobs />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/affinity-perf"
              element={
                <ProtectedRoute requireRole={["superadmin"]}>
                  <AffinityPerf />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/demo-data"
              element={
                <ProtectedRoute requireRole={["superadmin"]}>
                  <DemoData />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/deletion-requests"
              element={
                <ProtectedRoute requireRole={["superadmin"]}>
                  <AccountDeletionRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/legal-versions"
              element={
                <ProtectedRoute requireRole={["superadmin"]}>
                  <LegalVersions />
                </ProtectedRoute>
              }
            />

            {/* Phase 0 — Foundation routes (additive) */}
            <Route path="foundation" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><FoundationOverview /></ProtectedRoute>} />
            <Route path="foundation/notifications" element={<ProtectedRoute><NotificationsInbox /></ProtectedRoute>} />
            <Route path="foundation/organizations" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Organizations /></ProtectedRoute>} />
            <Route path="foundation/companies" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Companies /></ProtectedRoute>} />
            <Route path="foundation/branches" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Branches /></ProtectedRoute>} />
            <Route path="foundation/stores" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Stores /></ProtectedRoute>} />
            <Route path="foundation/store-setup" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><StoreOnboardingWizard /></ProtectedRoute>} />
            <Route path="foundation/countries" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Countries /></ProtectedRoute>} />
            <Route path="foundation/currencies" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Currencies /></ProtectedRoute>} />
            <Route path="foundation/exchange-rates" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><ExchangeRates /></ProtectedRoute>} />
            <Route path="foundation/languages" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Languages /></ProtectedRoute>} />
            <Route path="foundation/timezones" element={<ProtectedRoute requirePermission="FOUNDATION.VIEW"><Timezones /></ProtectedRoute>} />
            <Route path="foundation/subscription-plans" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><SubscriptionPlans /></ProtectedRoute>} />
            <Route path="foundation/feature-flags" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><FeatureFlags /></ProtectedRoute>} />
            <Route path="foundation/configurations" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><Configurations /></ProtectedRoute>} />
            <Route path="foundation/number-series" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><NumberSeries /></ProtectedRoute>} />
            <Route path="foundation/notification-templates" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><NotificationTemplates /></ProtectedRoute>} />
            <Route path="foundation/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
            <Route path="foundation/ai-models" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><AIModels /></ProtectedRoute>} />
            <Route path="foundation/prompts" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><Prompts /></ProtectedRoute>} />
            <Route path="foundation/api-registry" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><ApiRegistry /></ProtectedRoute>} />

            {/* Phase 0A — UX Foundation routes (additive) */}
            <Route path="design-system" element={<ProtectedRoute><DesignSystem /></ProtectedRoute>} />
            <Route path="settings/appearance" element={<ProtectedRoute><Appearance /></ProtectedRoute>} />
            <Route path="settings/branding" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><BrandingSettings /></ProtectedRoute>} />
            <Route path="settings/devices" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><DeviceSettings /></ProtectedRoute>} />
            <Route path="settings/communications" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><CommunicationsSettings /></ProtectedRoute>} />
            <Route path="settings/payments" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PaymentGatewaySettings /></ProtectedRoute>} />
            <Route path="pos-setup" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PosSetupWizard /></ProtectedRoute>} />
            <Route path="pos-fast-login" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><PosFastLogin /></ProtectedRoute>} />
            <Route path="store-today" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><RouteErrorBoundary name="Store Today"><StoreOpsToday /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="salesperson-performance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><RouteErrorBoundary name="Salesperson Performance"><SalespersonPerformance /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="pos-ops" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><PosOpsHome /></ProtectedRoute>} />
            <Route path="pos-admin" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PosAdminSettings /></ProtectedRoute>} />

            {/* Phase 0B — Reporting Framework routes (additive) */}
            <Route path="reports" element={<ProtectedRoute><ReportCatalog /></ProtectedRoute>} />
            <Route path="reports/ai-builder" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AIReportBuilder /></ProtectedRoute>} />
            <Route path="reports/schedules" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ReportSchedules /></ProtectedRoute>} />
            <Route path="reports/analytics" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ReportAnalytics /></ProtectedRoute>} />
            <Route path="reports/visibility" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><ReportVisibilityMatrix /></ProtectedRoute>} />
            <Route path="reports/module/:moduleKey" element={<ProtectedRoute><ModuleReports /></ProtectedRoute>} />
            <Route path="report-designer" element={<ProtectedRoute><ReportDesigner /></ProtectedRoute>} />
            <Route path="reports/:id" element={<ProtectedRoute><ReportViewer /></ProtectedRoute>} />

            {/* Phase 0C — Workflow / BPM routes (additive) */}
            <Route path="workflows" element={<ProtectedRoute><WorkflowCatalog /></ProtectedRoute>} />
            <Route path="workflows/inbox" element={<ProtectedRoute><WorkflowInbox /></ProtectedRoute>} />
            <Route path="workflows/analytics" element={<ProtectedRoute requireRole={["superadmin","admin","manager"]}><WorkflowAnalytics /></ProtectedRoute>} />
            <Route path="workflows/rules" element={<ProtectedRoute requireRole={["superadmin","admin","manager"]}><RulesLibrary /></ProtectedRoute>} />
            <Route path="workflows/instances/:id" element={<ProtectedRoute><WorkflowInstance /></ProtectedRoute>} />

            {/* Phase 1 — Security & Identity routes (additive) */}
            <Route path="security" element={<ProtectedRoute requirePermission="SECURITY.VIEW"><SecurityCenter /></ProtectedRoute>} />
            <Route path="admin/route-authz" element={<ProtectedRoute requirePermission="SECURITY.VIEW"><RouteAuthz /></ProtectedRoute>} />
            <Route path="security/me" element={<ProtectedRoute><MySecurity /></ProtectedRoute>} />
            <Route path="admin/backup" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><BackupDR /></ProtectedRoute>} />

            {/* Phase 2 — Product Foundation routes (additive) */}
            <Route path="products" element={<ProtectedRoute><ProductFoundation /></ProtectedRoute>} />
            <Route path="products/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />

            {/* Phase 2A — PIM Studio routes (additive) */}
            <Route path="pim" element={<ProtectedRoute><PimStudio /></ProtectedRoute>} />
            <Route path="pim/attribute-sets" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AttributeSets /></ProtectedRoute>} />
            <Route path="pim/taxonomy" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><TaxonomyManager /></ProtectedRoute>} />
            <Route path="pim/classification" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ClassificationSystems /></ProtectedRoute>} />
            <Route path="pim/catalogs" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><Catalogs /></ProtectedRoute>} />
            <Route path="pim/channels" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><Channels /></ProtectedRoute>} />
            <Route path="pim/marketplaces" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><Marketplaces /></ProtectedRoute>} />
            <Route path="pim/publications" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PublicationCenter /></ProtectedRoute>} />
            <Route path="pim/validation-rules" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ValidationRules /></ProtectedRoute>} />
            <Route path="pim/content-templates" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ContentTemplates /></ProtectedRoute>} />
            <Route path="pim/approvals" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ApprovalCenter /></ProtectedRoute>} />
            <Route path="pim/quality" element={<ProtectedRoute><QualityCenter /></ProtectedRoute>} />

            {/* Phase 2B — DAM Studio routes (additive) */}
            <Route path="dam" element={<ProtectedRoute><DamStudio /></ProtectedRoute>} />
            <Route path="dam/repository" element={<ProtectedRoute><AssetRepository /></ProtectedRoute>} />
            <Route path="dam/assets/:id" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />
            <Route path="dam/collections" element={<ProtectedRoute><CollectionManager /></ProtectedRoute>} />
            <Route path="dam/approvals" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DamApprovalCenter /></ProtectedRoute>} />
            <Route path="dam/storage" element={<ProtectedRoute><StorageManager /></ProtectedRoute>} />

            {/* Phase 2C — MDM Studio routes (additive) */}
            <Route path="mdm" element={<ProtectedRoute><MdmStudio /></ProtectedRoute>} />
            <Route path="mdm/domains" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DomainManager /></ProtectedRoute>} />
            <Route path="mdm/golden" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><GoldenRecordManager /></ProtectedRoute>} />
            <Route path="mdm/reference-data" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ReferenceDataManager /></ProtectedRoute>} />
            <Route path="mdm/hierarchies" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><HierarchyManager /></ProtectedRoute>} />
            <Route path="mdm/duplicates" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DuplicateCenter /></ProtectedRoute>} />
            <Route path="mdm/merges" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MergeWorkbench /></ProtectedRoute>} />
            <Route path="mdm/quality" element={<ProtectedRoute><MdmQualityCenter /></ProtectedRoute>} />
            <Route path="mdm/governance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><GovernanceCenter /></ProtectedRoute>} />
            <Route path="mdm/steward" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><StewardWorkbench /></ProtectedRoute>} />
            <Route path="mdm/lineage" element={<ProtectedRoute><LineageExplorer /></ProtectedRoute>} />
            <Route path="mdm/ai" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MdmAiStudio /></ProtectedRoute>} />

            {/* Phase 3 — Customer Foundation routes (additive) */}
            <Route path="customers" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><CustomerMaster /></ProtectedRoute>} />
            <Route path="customers/structure" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CustomerStructure /></ProtectedRoute>} />
            <Route path="customers/dashboard" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CustomerDashboard /></ProtectedRoute>} />
            <Route path="customers/:id" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><CustomerProfile /></ProtectedRoute>} />

            {/* Phase 4 — Supplier & Vendor Foundation routes (additive) */}
            <Route path="vendors" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorMaster /></ProtectedRoute>} />
            <Route path="vendors/structure" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorStructure /></ProtectedRoute>} />
            <Route path="vendors/governance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorGovernance /></ProtectedRoute>} />
            <Route path="vendors/performance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorPerformance /></ProtectedRoute>} />
            <Route path="vendors/dashboard" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorDashboard /></ProtectedRoute>} />
            <Route path="vendors/:id" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VendorProfile /></ProtectedRoute>} />

            {/* Phase 24 — Enterprise Integration Fabric */}
            <Route path="integration" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><IntegrationFabric /></ProtectedRoute>} />

            {/* Phase 25 — Enterprise Analytics Platform */}
            <Route path="analytics" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AnalyticsHome /></ProtectedRoute>} />
            <Route path="target-management" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><TargetManagement /></ProtectedRoute>} />
            <Route path="incentive-schemes" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><IncentiveSchemes /></ProtectedRoute>} />
            <Route path="my-performance" element={<ProtectedRoute requirePermission="SALESPERSON.VIEW_OWN"><MyPerformance /></ProtectedRoute>} />
            <Route path="control-tower" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "employee"]}><ControlTowerHome /></ProtectedRoute>} />
            <Route path="executive" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ExecutiveHome /></ProtectedRoute>} />
            <Route path="digital-twin" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DigitalTwinHome /></ProtectedRoute>} />
            <Route path="command-center" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CommandCenterHome /></ProtectedRoute>} />
            <Route path="work-hub" element={<WorkHub />} />
            <Route path="finance/company-link" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CompanyLink /></ProtectedRoute>} />
            <Route path="product-registry" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><ProductRegistryHome /></ProtectedRoute>} />
            <Route path="commercial-governance" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><CommercialGovernanceHome /></ProtectedRoute>} />
            <Route path="experience" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><ExperienceHome /></ProtectedRoute>} />
            <Route path="my-workspace" element={<MyWorkspace />} />
            <Route path="security-governance" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><SecurityHome /></ProtectedRoute>} />
            <Route path="ai-consumption" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><AiConsumptionHome /></ProtectedRoute>} />
            <Route path="dsar" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><DSARConsole /></ProtectedRoute>} />
            <Route path="my-ai-usage" element={<TenantConsumptionPortal />} />
            <Route path="app-exchange" element={<ProtectedRoute requireRole={["superadmin", "admin"]}><MarketEcosystemHome /></ProtectedRoute>} />

            {/* Phase 26 — Enterprise Data Platform & Lakehouse */}
            <Route path="data-platform" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DataPlatformHome /></ProtectedRoute>} />

            {/* Phase 5 — Enterprise Inventory Foundation */}
            <Route path="inventory-platform" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><InventoryFoundation /></ProtectedRoute>} />

            {/* Phase 6 — Enterprise Warehouse Management System */}
            <Route path="wms" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><WmsHome /></ProtectedRoute>} />

            {/* Phase 7 — Enterprise Procurement Management */}
            <Route path="procurement" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ProcurementHome /></ProtectedRoute>} />

            {/* Phase 8 — Enterprise Merchandising Foundation */}
            <Route path="merchandising" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MerchandisingHome /></ProtectedRoute>} />

            {/* Phase 8A — Merchandise Financial Planning & Open-To-Buy */}
            <Route path="mfp" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MfpHome /></ProtectedRoute>} />
            <Route path="allocation" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AllocationHome /></ProtectedRoute>} />
            <Route path="assortment" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AssortmentHome /></ProtectedRoute>} />
            <Route path="replenishment" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ReplenishmentHome /></ProtectedRoute>} />
            <Route path="pricing" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PricingHome /></ProtectedRoute>} />
            <Route path="promotions" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><PromotionHome /></ProtectedRoute>} />
            <Route path="pos-core" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><RouteErrorBoundary name="POS Checkout"><PosCoreHome /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="offline-pos" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><RouteErrorBoundary name="Offline POS"><OfflineHome /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="mpos" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><RouteErrorBoundary name="Mobile POS"><MposHome /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="crash-test" element={<ProtectedRoute><RouteErrorBoundary name="Crash Test"><CrashProbe /></RouteErrorBoundary></ProtectedRoute>} />
            <Route path="edge" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><EdgeHome /></ProtectedRoute>} />
            <Route path="dmp" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DmpHome /></ProtectedRoute>} />
            <Route path="hardware" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><HardwareHome /></ProtectedRoute>} />
            <Route path="sco" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ScoHome /></ProtectedRoute>} />
            <Route path="scango" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><ScangoHome /></ProtectedRoute>} />
            <Route path="smart-checkout" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><SmartcoHome /></ProtectedRoute>} />
            <Route path="ai-checkout" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AicoHome /></ProtectedRoute>} />
            <Route path="loss-prevention" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><LpHome /></ProtectedRoute>} />
            <Route path="rfid" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><RfidHome /></ProtectedRoute>} />
            <Route path="vision" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><VisionHome /></ProtectedRoute>} />
            <Route path="audit" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AuditHome /></ProtectedRoute>} />
            <Route path="omnichannel" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><OmniHome /></ProtectedRoute>} />
            <Route path="marketplace" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MarketplaceHome /></ProtectedRoute>} />
            <Route path="seller-platform" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><SellerPlatformHome /></ProtectedRoute>} />
            <Route path="loyalty" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><LoyaltyHome /></ProtectedRoute>} />
            <Route path="crm" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CrmHome /></ProtectedRoute>} />
            <Route path="marketing" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><MarketingHome /></ProtectedRoute>} />
            <Route path="cdp" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CdpHome /></ProtectedRoute>} />
            <Route path="cxm" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CxmHome /></ProtectedRoute>} />
            <Route path="finance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><FinanceHome /></ProtectedRoute>} />
            <Route path="advanced-finance" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AdvFinHome /></ProtectedRoute>} />
            <Route path="workforce" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><WorkforceHome /></ProtectedRoute>} />
            <Route path="supply-chain" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><SupplyChainHome /></ProtectedRoute>} />
            <Route path="transportation" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><TransportationHome /></ProtectedRoute>} />
            <Route path="commercial" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><CommercialHome /></ProtectedRoute>} />
            <Route path="ai-platform" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AiHome /></ProtectedRoute>} />
            <Route path="genai" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><GenaiHome /></ProtectedRoute>} />
            <Route path="agentic" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><AgenticHome /></ProtectedRoute>} />
            <Route path="decisionos" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><DecisionHome /></ProtectedRoute>} />
            <Route path="rems" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager"]}><RemsHome /></ProtectedRoute>} />
            <Route path="tailor" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier", "employee"]}><TailorHome /></ProtectedRoute>} />
            <Route path="help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
          </Route>

          {/* Phase 10 — Self Checkout customer kiosk (public, station-provisioned) */}
          <Route path="/kiosk" element={<KioskApp />} />

          {/* P13 — Cashier fast-login lock screen (terminal gate, pre-session) */}
          <Route path="/pos-lock" element={<PosLockScreen />} />

          {/* Phase 6B — dedicated full-screen cashier POS shell (SELL/RETURN/ORDERS/CUSTOMER/TILL/MORE) */}
          <Route path="/pos-app" element={<ProtectedRoute requireRole={["superadmin", "admin", "manager", "cashier"]}><RouteErrorBoundary name="POS"><PosShell /></RouteErrorBoundary></ProtectedRoute>} />

          {/* Real-device — public customer/pole display (secondary window, no login) */}
          <Route path="/customer-display/:stationId" element={<CustomerDisplay />} />

          {/* Phase 10A — Scan & Go mobile shopper (public, mobile-provisioned) */}
          <Route path="/shop" element={<ShopApp />} />

          {/* Phase 10B — Smart Checkout autonomous / walk-out (public, mobile-provisioned) */}
          <Route path="/smart-store" element={<SmartStoreApp />} />

          {/* Phase 10C — AI Shopping Assistant (public, mobile-provisioned) */}
          <Route path="/ai-shopper" element={<AiShopperApp />} />

          {/* Phase 4F — External Vendor / Supplier Portal (separate auth context) */}
          <Route path="/vendor-portal/login" element={<VendorPortalProvider><VendorPortalLogin /></VendorPortalProvider>} />
          <Route path="/vendor-portal" element={<VendorPortalProvider><VendorPortal /></VendorPortalProvider>} />

          {/* REASON TailorStudio™ — public customer order-tracking portal (order code + mobile) */}
          <Route path="/tailor-portal" element={<TailorPortal />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </ConfirmProvider>
      </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </UxProvider>
  );
}
